import type { ReactNode } from 'react';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { useTranslation } from 'react-i18next';
import InfoSection from '@/components/Approval/@components/InfoSection';
import NewWindowIcon from '@/public/icons/approval/new-window.svg';
import type { CancelApprovalAttributes } from '@/types/approval';
import {
  CancelTargetBox,
  CancelTargetDate,
  CancelTargetInfoRoot,
  CancelTargetLinkAnchor,
  CancelTargetSubject,
} from './styles';

const EMPTY = '-';

/** "2026-06-01T…" / "2026-06-01 12:00:00" → "2026-06-01". 그 외 포맷은 원문 유지 */
export const formatCancelTargetDate = (
  value: string | null | undefined,
): string => {
  if (!value) return EMPTY;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : value;
};

type CancelTargetInfoProps = {
  title?: ReactNode;
  /** 취소 대상 원 품의. 없으면 message 를 박스 자리에 표시 */
  target?: Pick<
    CancelApprovalAttributes,
    'subject' | 'submittedAt' | 'url'
  > | null;
  /** 대상 없음·조회 실패·취소 불가 안내 */
  message?: ReactNode;
  /** 원문 열람 링크(새 탭) 노출 — 작성 화면 전용. 조회 슬롯은 레거시대로 미노출 */
  showLink?: boolean;
  className?: string;
};

/**
 * 근태 취소 품의 「취소할 품의」 박스 — 작성 필드·조회 슬롯 공용.
 * 원 품의 제목 + 상신일 한 줄, 선택적으로 원문 링크.
 */
const CancelTargetInfo = ({
  title,
  target,
  message,
  showLink,
  className,
}: CancelTargetInfoProps) => {
  const { t } = useTranslation();

  return (
    <InfoSection title={title} className={className}>
      <CancelTargetInfoRoot>
        {target ? (
          <>
            <CancelTargetBox>
              <CancelTargetSubject>
                {target.subject || EMPTY}
              </CancelTargetSubject>
              <CancelTargetDate>
                {formatCancelTargetDate(target.submittedAt)}
              </CancelTargetDate>
            </CancelTargetBox>
            {showLink && target.url && (
              <CancelTargetLinkAnchor
                href={target.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('품의 내용 확인')}
                <NewWindowIcon width={12} height={12} />
              </CancelTargetLinkAnchor>
            )}
          </>
        ) : (
          <ApprovalNoticeBox role="status">
            {message ?? EMPTY}
          </ApprovalNoticeBox>
        )}
      </CancelTargetInfoRoot>
    </InfoSection>
  );
};

export default CancelTargetInfo;
